import { Button, Card, Input, InputNumber, Message, Space, Table, Modal } from "@arco-design/web-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IconEdit } from "@arco-design/web-react/icon";
import { Number, Currency } from "@/components/Numeric";
import { ColorfulTable } from "@/components/ColorfulTable";
import { PresetColorPicker } from "@/components/PresetColorPicker";
import { POAPI } from "@/apis/purchasing";
import { FileUtil, UserProfile, UserProfileType } from "@/utils";
import _ from "lodash";
import dayjs from "dayjs";
import { POStatusDef, POStatus } from "@/enums/purchasing";
import { EnumLabel } from "@/components/Enum";
import Reference from "@/components/Reference";

const EditableNote = (props) => {
    const { onChange, record } = props;
    const [note, setNote] = useState(record.note);
    const [newNote, setNewNote] = useState(record.note);
    const [editing, setEditing] = useState(false);

    const save = async () => {
        await POAPI.updateNote(record.id, newNote);
        onChange && onChange(newNote);
        setNote(newNote);
    }

    return (
        <div>
            <span>{note}</span>
            <Button
                type="text"
                size="mini"
                icon={<IconEdit />}
                onClick={() => setEditing(true)} />
            <Modal
                title="Note"
                visible={editing}
                onOk={async () => {
                    await save();
                    setEditing(false);
                }}
                onCancel={() => {
                    setEditing(false);
                    setNewNote(note);
                }}
            >
                <Input.TextArea
                    value={newNote}
                    onChange={(value) => {
                        setNewNote(value);
                    }} />
            </Modal>
        </div>
    );
}

export const OpenPOReport = () => {
    const [totalOpenQuantity, setTotalOpenQuantity] = useState(0);
    const [totalOpenAmount, setTotalOpenAmount] = useState(0);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState();
    const [pagination, setPagination] = useState();
    const [collapsed, setCollapsed] = useState(true);
    const [expandedItems, setExpandedItems] = useState();
    const [dueDateColor, setDueDateColor] = useState();
    const [dueDateDays, setDueDateDays] = useState();
    const [etaColor, setEtaColor] = useState();
    const [etaDays, setEtaDays] = useState();
    const [page, setPage] = useState(1);
    const [orderBy, setOrderBy] = useState();
    const [orderDesc, setOrderDesc] = useState(false);

    const originColumns = useMemo(() => [
        {
            title: "PO#",
            dataIndex: "poNo",
            render: (col, record) => <Link to={`/workspace/purchasing/po/${record.id}`}>{record.poNo}</Link>,
            sorter: true,
            width: 110
        }, {
            title: "Status",
            dataIndex: "status",
            render: (col, record) => <EnumLabel enumObj={POStatusDef} value={record.status} />,
            width: 100
        }, {
            title: "Vendor",
            dataIndex: "vendorName",
        }, {
            title: "PO Date",
            dataIndex: "poDate",
            sorter: true,
            width: 105,
        }, {
            title: <div>
                <div>Due Date</div>
                <div>
                    <InputNumber hideControl
                        placeholder="Days"
                        size="mini"
                        min={0}
                        style={{ width: 45 }}
                        value={dueDateDays}
                        onChange={(val) => setDueDateDays(val)} />

                    <PresetColorPicker
                        value={dueDateColor}
                        onChange={(color) => {
                            setDueDateColor(color);
                        }}
                    />
                </div>
            </div>,
            width: 105,
            dataIndex: "dueDate",
            bgColor: dueDateColor,
            colorful: (record) => dayjs(record.dueDate).isBefore(dayjs().add(dueDateDays ?? 0, 'day'))
        }, {
            title: <div>
                <div>ETA</div>
                <InputNumber hideControl
                    placeholder="Days"
                    size="mini"
                    min={0}
                    style={{ width: 45 }}
                    value={etaDays}
                    onChange={val => setEtaDays(val)} />

                <PresetColorPicker
                    value={etaColor}
                    onChange={(color) => {
                        setEtaColor(color);
                    }}
                />
            </div>,
            width: 105,
            dataIndex: "eta",
            bgColor: etaColor,
            colorful: (record) => dayjs(record.eta).isBefore(dayjs().add(etaDays ?? 0, 'day'))
        }, {
            title: "Reference",
            render: (col, record) => <Reference value={record.reference} />,
            width: 180,
        }, {
            title: "Open Amount",
            render: (col, record) => <Currency value={record.openAmount} />,
        }, {
            title: "Note",
            render: (col, record) => <EditableNote record={record} column={col} />,
        }
    ], [dueDateColor, dueDateDays, etaColor, etaDays]);

    const getData = useCallback(async () => {
        const result = await POAPI.report({
            page: page ?? 1,
            orderBy,
            orderDesc,
            status: [POStatus.Created, POStatus.Issued, POStatus.Partial]
        });
        const { data, totalQuantity, totalAmount } = result;
        setData(data.records);
        setPagination(pagination => ({ ...pagination, total: data.total }));
        setTotalOpenQuantity(totalQuantity);
        setTotalOpenAmount(totalAmount);
    }, [page, orderBy, orderDesc])

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.OPEN_PO_REPORT, {
            collapsed,
            dueDateColor,
            dueDateDays,
            etaColor,
            etaDays,
        });
    }

    const loadColumns = useCallback(() => {
        const cols = _.map(originColumns, (val, index) => {
            const column = { ...val };
            if (column.dataIndex && orderDesc != null && orderBy === column.dataIndex) {
                column.sortOrder = orderDesc ? "descend" : "ascend";
            } else {
                column.sortOrder = undefined;
            }

            return { ...column };
        });
        setColumns(cols);
    }, [originColumns, orderBy, orderDesc]);

    useEffect(() => {
        loadColumns();
    }, [loadColumns]);

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        const loadProfile = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.OPEN_PO_REPORT);
            if (profile) {
                profile.dueDateColor && setDueDateColor(profile.dueDateColor);
                profile.dueDateDays && setDueDateDays(profile.dueDateDays);
                profile.etaColor && setEtaColor(profile.etaColor);
                profile.etaDays && setEtaDays(profile.etaDays);
                profile.collapsed && setCollapsed(profile.collapsed);
            }
        };
        loadProfile();
    }, []);

    const expandedRowRender = (record) => {
        return <Table rowKey="id"
            columns={[
                {
                    title: "Item Name",
                    dataIndex: "itemName",
                }, {
                    title: "Item Description",
                    dataIndex: "description",
                }, {
                    title: "PO Qty",
                    render: (col, record) => <Number value={record.quantity} />,
                }, {
                    title: "Open Qty",
                    render: (col, record) => <Number value={record.openQuantity} />,
                }, {
                    title: "Rate",
                    render: (col, record) => <Currency value={record.rate} />
                }, {
                    title: "Amount",
                    render: (col, record) => <Currency value={record.openQuantity * record.rate} />
                }
            ]}
            pagination={false}
            data={_.filter(record.items, (item) => item.openQuantity > 0)} />;
    };

    return (
        <Card title="Open PO Report" extra={<Space>
            <Button type="primary" onClick={() => {
                const state = !collapsed;
                setCollapsed(state);
                if (state) {
                    setExpandedItems([]);
                } else {
                    setExpandedItems(_.map(data, item => item.id));
                }
            }}>{collapsed ? "Expand" : "Collapse"}</Button>
            <Button type="primary" onClick={async () => {
                const res = await POAPI.export({ status: [POStatus.Created, POStatus.Issued, POStatus.Partial] });
                FileUtil.download(res.data, "Open PO Report.xlsx");
            }}>Export</Button>
            <Button type="primary" onClick={async () => {
                await saveProfile();
                Message.success("Profile Saved");
            }}>Save Profile</Button>
        </Space>}>
            <Space>
                <span>Total Open Qty: <Number value={totalOpenQuantity} /></span>
                <span>Total Open Amount: <Currency value={totalOpenAmount} /></span>
            </Space>
            <ColorfulTable rowKey="id"
                style={{ marginTop: 5 }}
                columns={columns}
                indentSize={60}
                expandedRowKeys={expandedItems}
                expandedRowRender={expandedRowRender}
                onExpandedRowsChange={(rows) => {
                    setExpandedItems(rows);
                }}
                scroll={{ y: 'calc(100vh - 320px)' }}
                data={data}
                pagination={pagination}
                onChange={(value, sorter) => {
                    const orderBy = sorter.direction ? sorter.field : null;
                    const orderDesc = sorter.direction ? (sorter.direction === "descend") : null;
                    setPagination(value);
                    setPage(value.current);
                    setOrderBy(orderBy);
                    setOrderDesc(orderDesc);
                }} />
        </Card>
    );
}
